import { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/reducers/index";
import { showSnackbar } from "../../redux/actions/index";

// Material UI
import { 
  Box, Card, CssBaseline, CircularProgress, 
  Typography, Chip, Avatar, Tooltip, IconButton 
} from "@mui/material";
import { 
  DataGrid, 
  GridColDef, 
  GridToolbarContainer, 
  GridToolbarExport 
} from "@mui/x-data-grid";

// Icons & Utils
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { extractActionColor, extractCodeColor, extractActionIcon } from "../../tools/history";

// Components
import NavTab from "../../layout/NavTab";
import FiltersWrapper from "../../components/History/FiltersWrapper";
import CustomNoRowsOverlay from "../../components/CustomNoRowsOverlay";
import { IHistory } from "../../interface/history";
import { fetchPostProtect } from "../../fetchs/post";

const History = () => {
  const currentDate = dayjs();
  const [ready, setReady] = useState<boolean>(false);
  const [data, setData] = useState<IHistory[]>([]);
  const [total, setTotal] = useState<number>(0);
  
  // Pagination
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });

  // Filtres (Passés au FiltersWrapper)
  const [currentNames, setCurrentNames] = useState<string[]>([]);
  const [currentActions, setCurrentActions] = useState<string[]>([]);
  const [currentStatus, setCurrentStatus] = useState<string[]>([]);
  const [currentEntities, setCurrentEntities] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Dayjs>(currentDate.subtract(15, "day").startOf("day"));
  const [endDate, setEndDate] = useState<Dayjs>(currentDate.endOf("day"));

  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.token);

  const postSearch = () => {
    setReady(false);
    const body = {
      names: currentNames,
      actions: currentActions,
      status: currentStatus,
      entities: currentEntities,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    };

    if (token) {
      fetchPostProtect(token, "/history/searchPaginated", JSON.stringify(body))
        .then((res) => {
          if (res.status === 200) {
            res.json().then((response) => {
              setData(response.data);
              setTotal(response.total);
              setReady(true);
            });
          }
        })
        .catch(() => setReady(true));
    }
  };

  // Déclenchement automatique de la recherche
  useEffect(() => { postSearch(); }, [paginationModel, startDate, endDate, currentActions, currentNames, currentStatus, currentEntities]);

  // --- COLONNES (Basées sur ton HistoryGrid) ---
  const columns: GridColDef[] = [
    {
      field: "user",
      headerName: "Utilisateur",
      flex: 1,
      valueGetter: (params) => params.row.user?.email || "Système",
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#e2e8f0', color: '#475569' }}>
            {params.value?.charAt(0).toUpperCase()}
          </Avatar>
          <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#1e293b" }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 160,
      renderCell: (params) => (
        <Chip
          label={params.value.charAt(0) + params.value.slice(1).toLowerCase()}
          color={extractActionColor(params.value)}
          icon={extractActionIcon(params.value)}
          sx={{ fontWeight: 600, height: 24, fontSize: '0.75rem' }}
          variant="outlined"
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      valueGetter: (params) => params.row.status?.code,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={extractCodeColor(params.value?.toString())}
          variant="filled"
          size="small"
          sx={{ fontWeight: 800, fontSize: '0.7rem' }}
        />
      ),
    },
    {
      field: "affectedEntity",
      headerName: "Entité affectée",
      flex: 0.8,
      valueGetter: (params) => params.row.affectedEntity?.entityType || "-",
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "updatedAt",
      headerName: "Date",
      width: 170,
      renderCell: (params) => (
        <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600 }}>
          {dayjs(params.value).format("DD/MM/YYYY HH:mm")}
        </Typography>
      ),
    },
    {
      field: "details",
      headerName: "Infos",
      width: 70,
      sortable: false,
      renderCell: () => (
        <Tooltip title="Détails du log">
          <IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton>
        </Tooltip>
      )
    }
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <CssBaseline />
      <NavTab text="Journal d'audit" />

      <Card
        elevation={0}
        sx={{
          mx: 3, mb: 3, p: 2,
          borderRadius: "24px",
          border: "1px solid #e2e8f0",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 20px rgba(0,0,0,0.03)"
        }}
      >
        {/* Intégration de tes filtres en haut du tableau */}
        <Box sx={{ mb: 2 }}>
          <FiltersWrapper
            token={token} setHistoryData={setData} ready={ready} setReady={setReady} postSearch={postSearch}
            currentNames={currentNames} setCurrentNames={setCurrentNames}
            currentActions={currentActions} setCurrentActions={setCurrentActions}
            currentStatus={currentStatus} setCurrentStatus={setCurrentStatus}
            currentEntities={currentEntities} setCurrentEntities={setCurrentEntities}
            startDate={startDate} setStartDate={setStartDate}
            endDate={endDate} setEndDate={setEndDate}
          />
        </Box>

        <Box sx={{ flexGrow: 1, width: '100%' }}>
          <DataGrid
            rows={data}
            getRowId={(row) => row._id}
            columns={columns}
            rowCount={total}
            loading={!ready}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              toolbar: () => (
                <GridToolbarContainer sx={{ p: 1, justifyContent: 'flex-end' }}>
                  <GridToolbarExport sx={{ color: "#64748b", fontWeight: 600 }} />
                </GridToolbarContainer>
              ),
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "#f8fafc",
                color: "#64748b",
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: '0.5px'
              },
              "& .MuiDataGrid-cell": { borderColor: "#f1f5f9" },
              "& .MuiDataGrid-row:hover": { bgcolor: "#f8fafc" },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default History;